import { Fragment, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import {
  Nullable,
  distinct,
  floatVal,
  intVal,
} from '@jamesgmarks/utilities';
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { Charge } from '../../../../../../entities/hydra';
import { StyledTableCell, StyledTableRow } from '../../../parts/mui/StyledTables';
import { toDollarAmount } from '../../../../app-utils';
import { EHydraState } from '../../../../entity-interfaces/IInvoice';
import MultipleSelectCheckmarks from '../../../parts/mui/MultipleSelectCheckmarks';
import { Link } from 'react-router-dom';
import { SelectedCredit } from './types';
import { filterCharges, filterInvoiceOptions } from '../chargeSearchUtils';

// Helper functions extracted outside component for better performance
const getValidInvoiceIds = (charge: Charge): number[] => {
  return distinct(
    (charge.chargeHasTaxes ?? [])
      .filter((cht) => (
        ![EHydraState.deleted, EHydraState.unsent].includes(cht.invoice.hydraState)
      ))
      .map((cht) => cht.invoiceId),
  );
};

const calculateChargeAmounts = (charge: Charge) => {
  const subtotal = (floatVal(charge.proratedSubtotal) || 0) + (floatVal(charge.discountAmount) || 0);
  const tax = floatVal(charge.taxAmount) || 0;
  const total = subtotal + tax;
  return { subtotal, tax, total };
};

const createInvoiceOptions = (charge: Charge, invoiceIds: number[]) => {
  return invoiceIds
    .map((id) => {
      const chargeHasTax = charge.chargeHasTaxes?.find(
        (cht) => cht.invoiceId === id,
      );
      const label = chargeHasTax?.invoice?.invoiceNumber || `PV: ${id}`;
      return { label, value: `${id}` };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

interface ChargeSelectionModeProps {
  selectedSubscription: Nullable<ISubscriptions>;
  charges: Charge[];
  selectedCredits: SelectedCredit[];
  selectedChargeRowIndex: Nullable<number>;
  selectedChargeInvoiceIds: number[];
  onBackToClients: () => void;
  onChargeAdded: (charge: Charge, invoiceIds: number[]) => void;
  onChargeRemoved: (chargeId: number, subscriptionId: number) => void;
  onChargeRowSelected: (index: number) => void;
  setSelectedChargeInvoiceIds: React.Dispatch<React.SetStateAction<number[]>>;
  onNextToReview: () => void;
}

export const ChargeSelectionMode = ({
  selectedSubscription,
  charges,
  selectedCredits,
  selectedChargeRowIndex,
  selectedChargeInvoiceIds,
  onBackToClients,
  onChargeAdded,
  onChargeRemoved,
  onChargeRowSelected,
  setSelectedChargeInvoiceIds,
  onNextToReview,
}: ChargeSelectionModeProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!selectedSubscription) return null;

  const selectedSubscriptionCount = new Set(
    selectedCredits
      .map(selectedCredit => selectedCredit.subscription.id)
      .filter((subscriptionId): subscriptionId is number => subscriptionId != null),
  ).size;
  const selectedSubscriptions = Array.from(
    selectedCredits.reduce<Map<number, ISubscriptions>>((acc, selectedCredit) => {
      const subscriptionId = selectedCredit.subscription.id;
      if (subscriptionId != null && !acc.has(subscriptionId)) {
        acc.set(subscriptionId, selectedCredit.subscription);
      }
      return acc;
    }, new Map()).values(),
  );

  // Check if a charge is already selected
  const isChargeSelected = (charge: Charge): boolean => {
    return selectedCredits.some(sc => (
      sc.credit.generatedSubscriptionId === charge.id &&
      sc.subscription.id === selectedSubscription.id
    ));
  };

  // Determine what text to display on charge selection button
  const getChargeSelectionButtonText = (index: number): string => {
    const isCurrentRow = index === selectedChargeRowIndex;
    const hasSelectedInvoices = selectedChargeInvoiceIds.length > 0;

    if (!isCurrentRow) return 'Select';

    return hasSelectedInvoices
      ? `Add ${selectedChargeInvoiceIds.length} ${selectedChargeInvoiceIds.length === 1 ? 'Invoice' : 'Invoices'}`
      : 'Select Invoices';
  };

  const filteredCharges = filterCharges(charges, searchTerm);

  // Check if charge selection button should be disabled
  const isChargeSelectionButtonDisabled = (index: number, invoiceCount: number): boolean => {
    const isCurrentRow = index === selectedChargeRowIndex;
    const hasSelectedInvoices = selectedChargeInvoiceIds.length > 0;

    return !invoiceCount || (invoiceCount > 1 && !hasSelectedInvoices && isCurrentRow);
  };

  // Build complete configuration for charge selection button
  const buildChargeSelectionButtonConfig = (charge: Charge, index: number, invoiceCount: number) => {
    const isSelected = isChargeSelected(charge);
    const isCurrentRow = index === selectedChargeRowIndex;
    const hasSelectedInvoices = selectedChargeInvoiceIds.length > 0;

    if (isSelected) {
      return {
        text: (
          <>
            Added <Check fontSize="small" />
          </>
        ),
        color: 'success' as const,
        variant: 'contained' as const,
        disabled: false,
        onClick: () => onChargeRemoved(charge.id!, selectedSubscription.id!),
      };
    }

    const buttonColor: 'success' | 'primary' = isCurrentRow && hasSelectedInvoices ? 'success' : 'primary';

    return {
      text: getChargeSelectionButtonText(index),
      color: buttonColor,
      variant: 'contained' as const,
      disabled: isChargeSelectionButtonDisabled(index, invoiceCount),
      onClick: () => handleChargeButtonClick(charge, index, invoiceCount),
    };
  };

  // Handle charge button clicks with cleaner logic
  const handleChargeButtonClick = (charge: Charge, index: number, invoiceCount: number) => {
    const invoiceIds = getValidInvoiceIds(charge);

    // If selecting a different row with multiple invoices, show invoice selector
    if (selectedChargeRowIndex !== index && invoiceCount > 1) {
      setSelectedChargeInvoiceIds([]);
      onChargeRowSelected(index);
      return;
    }

    // Add charge with appropriate invoice IDs
    const targetInvoiceIds = invoiceCount === 1 ? invoiceIds : selectedChargeInvoiceIds;
    if (targetInvoiceIds.length > 0) {
      onChargeAdded(charge, targetInvoiceIds);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={8}>
          <Typography variant="h6">
            Choose Charges for: {selectedSubscription.invoiceDescription}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Subscription ID: {selectedSubscription.id}
          </Typography>
        </Grid>
        <Grid item xs={4} textAlign="right">
          <Button
            color="warning"
            onClick={onBackToClients}
            variant="outlined"
          >
            Back to Subscriptions
          </Button>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          Use Back to Subscriptions to add credits for another subscription, if needed, before reviewing.
        </Typography>
        {selectedSubscriptions.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              Selected subscriptions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
              {selectedSubscriptions.map(subscription => (
                <Chip
                  key={subscription.id}
                  color="success"
                  label={`${subscription.id} - ${subscription.invoiceDescription}`}
                  size="small"
                  sx={{
                    height: 'auto',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      display: 'block',
                      overflow: 'hidden',
                      py: 0.5,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'normal',
                    },
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Alert>

      {charges.length > 0 && (
       <Box sx={{ mt: 2, mb: 2 }}>
         <TextField
           label="Search by charge ID or invoice number"
           placeholder="Enter charge ID or invoice number..."
           value={searchTerm}
           onChange={(e) => setSearchTerm(e.target.value)}
           fullWidth
           size="small"
           sx={{ backgroundColor: 'white' }}
         />
       </Box>
      )}

      {charges.length === 0 ? (
        <Typography mt={3} variant="h6">
          <b><u>No Charges Available for This Subscription</u></b>
        </Typography>
      ) : (
        <>
          <TableContainer
            sx={{
              mt: 2,
              maxHeight: '50vh',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table stickyHeader sx={{ width: '100%', textAlign: 'center' }}>
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>Charge ID</StyledTableCell>
                  <StyledTableCell>Year</StyledTableCell>
                  <StyledTableCell>Month</StyledTableCell>
                  <StyledTableCell>Invoice Count</StyledTableCell>
                  <StyledTableCell>Subtotal</StyledTableCell>
                  <StyledTableCell>Tax</StyledTableCell>
                  <StyledTableCell>Total</StyledTableCell>
                  <StyledTableCell>&nbsp;</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {filteredCharges.map((charge, index) => {
                  const originalIndex = charges.findIndex(c => c.id === charge.id);
                  const invoiceIds = getValidInvoiceIds(charge);
                  const invoiceCount = invoiceIds.length;
                  const amounts = calculateChargeAmounts(charge);
                  const options = createInvoiceOptions(charge, invoiceIds);
                  const filteredOptions = filterInvoiceOptions(options, searchTerm);
                  const buttonConfig = buildChargeSelectionButtonConfig(charge, originalIndex, invoiceCount);

                  return (
                    <Fragment key={charge.id ?? index}>
                      <StyledTableRow hover={true}>
                        <StyledTableCell>
                          <Link to={`/charges/${charge.id}`} target="_blank">
                            {charge.id}
                          </Link>
                        </StyledTableCell>
                        <StyledTableCell>{charge.year}</StyledTableCell>
                        <StyledTableCell>{charge.month}</StyledTableCell>
                        <StyledTableCell>{invoiceCount}</StyledTableCell>
                        <StyledTableCell>
                          {toDollarAmount(amounts.subtotal)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {toDollarAmount(amounts.tax)}
                        </StyledTableCell>
                        <StyledTableCell>
                          {toDollarAmount(amounts.total)}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Button
                            color={buttonConfig.color}
                            size="small"
                            variant={buttonConfig.variant}
                            disabled={buttonConfig.disabled}
                            onClick={buttonConfig.onClick}
                          >
                            {buttonConfig.text}
                          </Button>
                        </StyledTableCell>
                      </StyledTableRow>
                      {selectedChargeRowIndex === originalIndex && (
                        <StyledTableRow>
                          <StyledTableCell align="right" colSpan={8}>
                            <MultipleSelectCheckmarks
                              options={filteredOptions}
                              inputLabelText="Invoices"
                              setSelectedValues={(stringValues) => {
                                // We know MultipleSelectCheckmarks only passes arrays, not functions
                                const values = Array.isArray(stringValues) ? stringValues : [];
                                const numberValues = values.map(val => intVal(val));
                                setSelectedChargeInvoiceIds(numberValues);
                              }}
                              onSelectionCommitted={(stringValues) => {
                                const numberValues = stringValues.map(val => intVal(val));
                                if (numberValues.length > 0) {
                                  onChargeAdded(charge, numberValues);
                                }
                              }}
                            />
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container sx={{ mt: 2 }} alignItems="center" spacing={2}>
            <Grid item xs={4} />
            <Grid item xs={4} textAlign="center">
              <Button
                color="primary"
                variant="contained"
                onClick={onNextToReview}
                disabled={selectedCredits.length === 0}
              >
                {selectedCredits.length === 0
                  ? 'No Credits Selected'
                  : `Review ${selectedCredits.length} Credit${selectedCredits.length !== 1 ? 's' : ''}`
                }
              </Button>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Typography variant="body2" color="text.secondary">
                {selectedSubscriptionCount} Subscription{selectedSubscriptionCount === 1 ? '' : 's'} Selected
              </Typography>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};
